import { useQuery } from '@tanstack/react-query';
import { parseISO, isAfter, format } from 'date-fns';
import { AnomalyDetectionLambdaFetch, ToolsLambdaFetch, awaitJSON } from '@src/utils/fetch-utils';
import {
  DEFAULT_CACHE_CONFIG,
  BudgetOption,
  BudgetRow,
  AlertFormData
} from '../anomalyDetectionConstants';

export const useBudgetOptions = (company: string): ReturnType<typeof useQuery<BudgetOption[]>> =>
  useQuery({
    queryKey: ['budgetIntakeOptions', company],
    queryFn: async (): Promise<BudgetOption[]> => {
      try {
        const response = await ToolsLambdaFetch('/getBudgetIntakeData', {
          params: {
            company
          }
        });

        const data = await awaitJSON<{ mappedBudgetEntries?: BudgetRow[] }>(response);

        const currentDate = new Date();
        const filteredData = data.mappedBudgetEntries?.filter(row => {
          if (row.goal_name !== 'Budget') {
            return false;
          }
          const endDate = parseISO(row.end_date ?? '');
          return !isAfter(currentDate, endDate);
        });

        const transformedData: BudgetOption[] =
          filteredData?.map(row => {
            const brandType = row['Brand vs Nonbrand'];
            const channel = row.Channel;
            const funnelTier = row['Funnel Tier'];
            const objective = row.Objective;
            const platform = row.Platform;
            const tactic = row.Tactic;
            const campaignPlatform = row.campaign_platform;
            const goalValue = row.goal_value;
            const goalValueFormatted =
              typeof goalValue === 'number'
                ? goalValue.toLocaleString('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    minimumFractionDigits: 0
                  })
                : undefined;
            const startDate = row.start_date;
            const endDate = row.end_date;

            let startDateFormatted: string | undefined;
            if (startDate) {
              const d = parseISO(startDate);
              if (!Number.isNaN(d.getTime())) {
                startDateFormatted = format(d, 'M/d/yyyy');
              }
            }

            let endDateFormatted: string | undefined;
            if (endDate) {
              const d = parseISO(endDate);
              if (!Number.isNaN(d.getTime())) {
                endDateFormatted = format(d, 'M/d/yyyy');
              }
            }

            let dateRange: string | undefined;
            if (startDateFormatted && endDateFormatted) {
              dateRange = `${startDateFormatted} - ${endDateFormatted}`;
            } else if (startDateFormatted) {
              dateRange = startDateFormatted;
            } else if (endDateFormatted) {
              dateRange = endDateFormatted;
            }

            const labelParts = [
              brandType,
              channel,
              funnelTier,
              objective,
              platform,
              tactic,
              campaignPlatform,
              goalValueFormatted,
              dateRange
            ].filter(val => {
              if (val === undefined || val === null || val === '' || val === 'N/A') {
                return false;
              }
              if (Array.isArray(val) && val.length === 1 && val[0] === null) {
                return false;
              }
              return true;
            });

            return {
              value: row.id?.toString() || '',
              label: labelParts.join(' | '),
              rowData: row
            };
          }) || [];

        return transformedData.sort((a, b) => {
          const endDateA = a.rowData?.end_date;
          const endDateB = b.rowData?.end_date;

          if (!endDateA && !endDateB) {
            return 0;
          }
          if (!endDateA) {
            return 1;
          }
          if (!endDateB) {
            return -1;
          }

          const dateA = parseISO(endDateA);
          const dateB = parseISO(endDateB);

          return dateB.getTime() - dateA.getTime();
        });
      } catch (error) {
        console.error('Failed to fetch budget options:', error);
        return [];
      }
    },
    enabled: Boolean(company),
    ...DEFAULT_CACHE_CONFIG
  });

export const useEmailAddresses = (): ReturnType<
  typeof useQuery<{ value: string; label: string }[]>
> =>
  useQuery({
    queryKey: ['emailAddresses'],
    queryFn: async (): Promise<{ value: string; label: string }[]> => {
      try {
        const response = await AnomalyDetectionLambdaFetch('/emails', {
          method: 'GET'
        });
        const data = await awaitJSON<{ emails?: string[] }>(response);

        return (
          data.emails?.map(email => ({
            value: email,
            label: email
          })) || []
        );
      } catch (error) {
        console.error('Failed to fetch email addresses:', error);
        return [{ value: 'keir.keenan@tinuiti.com', label: 'keir.keenan@tinuiti.com' }];
      }
    },
    ...DEFAULT_CACHE_CONFIG
  });

export const useSlackMembers = (): ReturnType<
  typeof useQuery<{ value: string; label: string }[]>
> =>
  useQuery({
    queryKey: ['slackMembers'],
    queryFn: async (): Promise<{ value: string; label: string }[]> => {
      try {
        const response = await AnomalyDetectionLambdaFetch('/slack-members', {
          method: 'GET'
        });
        const data = await awaitJSON<{ members?: { full_name: string; slack_user_id: string }[] }>(
          response
        );
        const uniqueMembers =
          data.members?.reduce<{ value: string; label: string }[]>((acc, member) => {
            const existingIndex = acc.findIndex(item => item.value === member.slack_user_id);
            if (existingIndex === -1) {
              acc.push({
                value: member.slack_user_id,
                label: member.full_name
              });
            }
            return acc;
          }, []) || [];

        return uniqueMembers;
      } catch (error) {
        console.error('Failed to fetch Slack members:', error);
        return [{ value: 'U0951JH99ED', label: 'Keir Keenan' }];
      }
    },
    ...DEFAULT_CACHE_CONFIG
  });

export const useSlackChannels = (): ReturnType<
  typeof useQuery<{ value: string; label: string }[]>
> =>
  useQuery({
    queryKey: ['slackChannels'],
    queryFn: async (): Promise<{ value: string; label: string }[]> => {
      try {
        const response = await AnomalyDetectionLambdaFetch('/slack-channels', {
          method: 'GET'
        });
        const data = await awaitJSON<{ channels?: string[] }>(response);
        const transformedChannels =
          data.channels
            ?.map(channel => {
              const cleanChannel = channel.startsWith('#') ? channel.slice(1) : channel;
              return {
                value: cleanChannel,
                label: `#${cleanChannel}`
              };
            })
            .filter(chan => !chan.value.startsWith('ext-')) || [];

        return transformedChannels;
      } catch (error) {
        console.error('Failed to fetch Slack channels:', error);
        return [{ value: 'bpm-pacing-anom-alerts', label: '#bpm-pacing-anom-alerts' }];
      }
    },
    ...DEFAULT_CACHE_CONFIG
  });

export const useUsedBudgetIds = (company: string): ReturnType<typeof useQuery<Set<number>>> =>
  useQuery({
    queryKey: ['usedBudgetIds', company],
    queryFn: async (): Promise<Set<number>> => {
      try {
        const response = await AnomalyDetectionLambdaFetch('/alerts', {
          method: 'GET',
          params: {
            company
          }
        });
        const data = await awaitJSON<{ alerts?: { parameters?: { budgetId?: number } }[] }>(
          response
        );

        const usedBudgetIds = new Set<number>();
        data.alerts?.forEach(alert => {
          if (alert.parameters?.budgetId) {
            usedBudgetIds.add(alert.parameters.budgetId);
          }
        });

        return usedBudgetIds;
      } catch (error) {
        console.error('Failed to fetch used budget IDs:', error);
        return new Set<number>();
      }
    },
    enabled: Boolean(company),
    ...DEFAULT_CACHE_CONFIG
  });

export interface AlertPreset {
  id: string;
  company: string;
  alert_type: string;
  preset_name: string;
  description: string;
  preset_data: AlertFormData;
  created_by_email: string;
  date_created: string;
  last_modified: string;
  last_modified_by_email: string | null;
}

export const useAlertPresets = (
  company: string,
  options?: { forTable?: boolean }
): ReturnType<typeof useQuery<AlertPreset[]>> =>
  useQuery({
    queryKey: options?.forTable ? ['alertPresetsTable', company] : ['alertPresets', company],
    queryFn: async (): Promise<AlertPreset[]> => {
      try {
        const response = await AnomalyDetectionLambdaFetch('/company-alert-presets', {
          method: 'GET',
          params: {
            company
          }
        });
        const data = await awaitJSON<AlertPreset[]>(response);
        return data || [];
      } catch (error) {
        console.error('Failed to fetch presets:', error);
        return [];
      }
    },
    enabled: Boolean(company),
    ...DEFAULT_CACHE_CONFIG
  });

export const useDeleteAlertPreset =
  () =>
  async (id: string): Promise<void> => {
    try {
      await AnomalyDetectionLambdaFetch(`/company-alert-presets/${id}`, {
        method: 'DELETE'
      });
    } catch (error) {
      console.error('Failed to delete preset:', error);
      throw error;
    }
  };
